import React, { useEffect, useMemo, useState } from "react";
import { Search, Forward, Loader2, Check, Users } from "lucide-react";
import Modal from "@/components/chat/Modal";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { getInitials, avatarColor } from "@/lib/avatar";
import { toast } from "sonner";

function payloadOf(m) {
  if (m.type === "text") return { type: "text", text: m.text, forwarded: true };
  if (m.type === "sticker") return { type: "sticker", text: m.text, forwarded: true };
  return { type: m.type, media: m.media, media_name: m.media_name, forwarded: true };
}

export default function ForwardDialog({ messages, currentChatId, onClose }) {
  const { t } = useI18n();
  const [chats, setChats] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/chats")
      .then(({ data }) => setChats(data.filter((c) => c.type !== "broadcast" && !c.readonly)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const filtered = useMemo(
    () => chats.filter((c) => (c.title || "").toLowerCase().includes(q.toLowerCase())),
    [chats, q]
  );

  const doForward = async () => {
    if (!sel.length) return;
    setBusy(true);
    try {
      for (const chatId of sel) {
        for (const m of messages) {
          await api.post("/messages", { chat_id: chatId, ...payloadOf(m) });
        }
      }
      toast.success(t("forwarded"));
      onClose();
    } catch (e) {
      toast.error("Forward failed");
    } finally {
      setBusy(false);
    }
  };

  const count = messages?.length || 0;

  return (
    <Modal title={t("forward_to")} onClose={onClose} bare>
      <div data-testid="forward-dialog" className="flex-1 min-h-0 flex flex-col max-w-2xl mx-auto w-full px-5 pt-4 pb-4 rc-safe-bottom">
        <div className="shrink-0">
        <p className="text-sm text-[var(--rc-muted)] mb-3">{count > 1 ? `${count} ${t("messages_label")}` : t("one_message_label")}</p>
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--rc-muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} data-testid="forward-search-input"
            className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
        </div>
        </div>

        <div className="flex-1 min-h-0 space-y-1 overflow-y-auto no-scrollbar pb-2">
          {loading ? (
            <div className="py-16 grid place-items-center text-[var(--rc-muted)]"><Loader2 className="animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-[var(--rc-muted)] py-10">{t("no_chats")}</p>
          ) : (
            filtered.map((c) => {
              const active = sel.includes(c.id);
              return (
                <button key={c.id} onClick={() => toggle(c.id)} data-testid={`forward-chat-${c.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left transition-colors ${active ? "bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/30" : "border border-transparent hover:bg-[var(--rc-surface)]"}`}>
                  <div className="h-10 w-10 rounded-full grid place-items-center text-[var(--rc-gold)] text-sm font-semibold overflow-hidden border-2 border-[var(--rc-gold)] shrink-0" style={{ backgroundColor: avatarColor(c.title) }}>
                    {c.avatar ? <img src={c.avatar} alt="" className="h-full w-full object-cover" /> : c.type === "group" ? <Users size={16} /> : getInitials(c.title)}
                  </div>
                  <span className="min-w-0 flex-1 truncate">{c.title}{c.id === currentChatId ? ` · ${t("this_chat")}` : ""}</span>
                  <span className={`h-6 w-6 shrink-0 rounded-full border grid place-items-center ${active ? "bg-[var(--rc-gold)] border-[var(--rc-gold)] text-black" : "border-[var(--rc-border)]"}`}>
                    {active && <Check size={14} />}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <button onClick={doForward} disabled={busy || sel.length === 0} data-testid="forward-send-btn"
          className="w-full mt-3 shrink-0 flex items-center justify-center gap-2 rounded-2xl bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Forward size={16} />} {t("forward")}{sel.length ? ` (${sel.length})` : ""}
        </button>
      </div>
    </Modal>
  );
}
